import { InfoOutlined, ModeCommentOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import Delete from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Tooltip from '@mui/material/Tooltip';
import {
	Box,
	Typography,
	TextField,
	MenuItem,
	RadioGroup,
	FormControlLabel,
	Radio,
	Checkbox,
	Button,
	FormControl,
	FormHelperText,
	Stack,
	InputLabel, AccordionDetails, AccordionSummary, Accordion,
} from "@mui/material";
import { DatePicker, TimePicker, DateTimePicker } from "@mui/x-date-pickers-pro";
import { NumericFormat } from "react-number-format";
import { FormikProvider } from "formik";
import { getIn } from "formik";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";
// import SidePanelLinks from "../../components/custom/app/sidePanelLinks/sidePanelLinks";
import CustomAutocomplete from "../../components/mui/form/autocomplete/autocomplete";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { API } from "../../helpers/constants";
// import { useGeolocation } from "../../hooks/useGeolocation";
import usePreviewForm from "../../hooks/usePreviewForm";
import useUserData from "../../hooks/user/useUserData";
import { QUESTIONNAIRE_BUILDER } from "../../locales/pages/namespaces";
import FileUpload from "./components/FileUpload";
import SignaturePad from "./components/SignaturePad";
import questionnaireFieldKey from "../../helpers/questionnaireFieldKey";

// Helper: render attachments that come from backend (filenames in public/uploads/questionnaires)
const QUESTIONNAIRE_UPLOADS_PUBLIC_PATH = "/uploads/questionnaires/";

const guessIsImageByName = (name) => {
	const ext = String(name || "").split(".").pop()?.toLowerCase();
	return ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext);
};

const normalizeBackendAttachments = (attachments) => {
	if (!attachments) return [];
	if (Array.isArray(attachments)) return attachments;
	return [attachments];
};

const attachmentToName = (att) => {
	if (att == null) return null;
	if (typeof att === "string") return att;
	return att.FileName || att.OriginFileName || att.name || att.filename || null;
};

const buildQuestionnaireAttachmentUrl = (fileName) => {
	if (!fileName) return "";
	if (/^https?:\/\//i.test(fileName)) return fileName;
	const safeName = String(fileName)
	.split("/")
	.map((seg) => encodeURIComponent(seg))
	.join("/");
	return `${QUESTIONNAIRE_UPLOADS_PUBLIC_PATH}${safeName}`;
};

const BackendAttachmentsLinks = ({ attachments, canDelete = false, onDelete }) => {
	const names = normalizeBackendAttachments(attachments)
	.map(attachmentToName)
	.filter(Boolean);
	if (names.length === 0) return null;
	
	const handleClick = (e, fileName) => {
		e.preventDefault();
		const url = buildQuestionnaireAttachmentUrl(fileName);
		if (!url) return;
		if (guessIsImageByName(fileName)) {
			window.open(url, "_blank");
			return;
		}
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		a.target = "_blank";
		a.rel = "noreferrer";
		a.click();
	};
	
	return (
		<Box sx={{ width: "100%", mt: 0.5 }}>
			{names.map((fileName) => (
				<Box key={fileName} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Typography variant="body2" sx={{ fontSize: 12, lineHeight: 1.25 }}>
						<a
							href={buildQuestionnaireAttachmentUrl(fileName)}
							target="_blank"
							rel="noreferrer"
							style={{ color: "#1976d2", textDecoration: "underline" }}
							onClick={(e) => handleClick(e, fileName)}
						>
							{fileName}
						</a>
					</Typography>
					{canDelete && (
						<IconButton
							edge="end"
							size="small"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onDelete?.(fileName);
							}}
						>
							<Delete fontSize="small"/>
						</IconButton>
					)}
				</Box>
			))}
		</Box>
	);
};

// Treat backend flags that may come as "1"/1/true
const toBool = (v) => v === "1" || v === 1 || v === true;

// --- Keys ---
// For repeatable question instances (e.g. same ItemId repeated inside repeatable groups),
// we must keep value/validation under a shared q:<ItemId> key, but comment/attachments UI
// should be per-instance to avoid collisions.
const getAggregateQuestionKey = (node) => {
	if (!node) return "";
	// Attributes are separate rows; use their own key.
	if (String(node?.Type) === 'attribute') return questionnaireFieldKey(node);
	// IMPORTANT: repeatable questions ALWAYS aggregate under q:<ItemId>, even when DetailId exists.
	// Initial values are built this way in usePreviewForm, so preview must read from the same key.
	if (toBool(node?.Repeatable)) {
		const base = node?.ItemId ?? node?.id;
		return base != null ? `q:${String(base)}` : questionnaireFieldKey(node);
	}
	// Non-repeatable answered question keeps row identity (d:<DetailId>)
	if (node?.DetailId != null) return questionnaireFieldKey(node);
	// Non-repeatable unanswered question: stable q:<ItemId>
	const base = node?.ItemId ?? node?.id;
	return base != null ? `q:${String(base)}` : questionnaireFieldKey(node);
};

const getInstanceUiKey = (node) => questionnaireFieldKey(node);

// Attributes render as their own fields; keep a stable key per row.
// - answered attributes: d:<DetailId>
// - dynamic attributes: questionnaireFieldKey provides a unique runtime key
const getAttributeFieldKey = (node) => questionnaireFieldKey(node);

/* Build tree */
function buildTree(items = []) {
	// Tree is based strictly on id/ParentId.
	// Items may share backend ids in repeatables, so clones/attributes must have unique `id`.
	const byId = new Map();
	const roots = [];
	
	(items || []).forEach((it) => {
		if (!it) return;
		const id = it.id;
		if (id == null || id === '') return;
		byId.set(String(id), {
			...it,
			Options: it.Options || it.options || [],
			children: [],
		});
	});
	
	(items || []).forEach((it) => {
		if (!it) return;
		const id = it.id;
		const node = byId.get(String(id));
		if (!node) return;
		const parentId = it.ParentId;
		if (parentId != null && parentId !== '') {
			const parent = byId.get(String(parentId));
			if (parent) {
				parent.children.push(node);
				return;
			}
		}
		roots.push(node);
	});
	console.log('buildRoots', roots);
	return roots;
}

const getFieldError = (formik, name, index) => {
	if (!formik || !name) return "";
	
	// Preferred: read exact indexed path (q:48[0]) so it matches how inputs are named.
	// Fallbacks for legacy/aggregated shapes:
	if (typeof index === "number") {
		const path = `${String(name)}[${index}]`;
		const t = getIn(formik.touched, path);
		const e = getIn(formik.errors, path);
		if (t && e) return e;
		
		const baseTouched = formik?.touched?.[name];
		const baseErr = formik?.errors?.[name];
		const itemTouched = Array.isArray(baseTouched) ? baseTouched[index] : Boolean(baseTouched);
		if (!itemTouched) return "";
		if (Array.isArray(baseErr)) return baseErr[index] || "";
		// If Yup returns a single string for the whole array, show it on index 0.
		if (typeof baseErr === "string") return index === 0 ? baseErr : "";
		return "";
	}
	
	const t = formik?.touched?.[name];
	const e = formik?.errors?.[name];
	return t && e ? e : "";
	
};

const Preview = ({
					 questionnaireId: propQuestionnaireId,
					 data: initialData,
					 onClose
				 }) => {
	const { t } = useTranslation(QUESTIONNAIRE_BUILDER);
	const params = useParams();
	// const geoStart = useGeolocation();
	// const geoEnd = useGeolocation();
	const questionnaireId = propQuestionnaireId || params.questionnaireId;
	const headerId = params?.headerId;
	
	// Repeatable groups: store cloned nodes here
	// NOTE: we no longer persist full clone nodes in mergedItems (it caused phantom groups and wrong attribute placement).
	const [openComments, setOpenComments] = useState({});
	const [dynamicItems, setDynamicItems] = useState([]);
	const [dismissedComments, setDismissedComments] = useState({});
	const [removedBackendAttachments, setRemovedBackendAttachments] = useState({});
	// activeRules: { [uiKey]: ruleId (1|2|3) }
	// Rule 1 = attachment + comment required
	// Rule 2 = attachment required
	// Rule 3 = comment required
	// Per-field (not per-index) so shared comment/attachments keys resolve correctly.
	const [activeRules, setActiveRules] = useState({});

	const { formik, loading, data, setData } = usePreviewForm({
		questionnaireId,
		headerId,
		data: initialData,
		// geoStart,
		// geoEnd,
		extraItems: dynamicItems,
		activeRules,
	});
	
	const {dateFormat, timeFormat} = useUserData();
	
	const toggleCommentVisibility = useCallback((uiKey) => {
		setOpenComments((prev) => {
			const currentlyOpen = Boolean(prev?.[uiKey]);
			const nextOpen = !currentlyOpen;
			
			if (!nextOpen) {
				// close: clear + dismiss
				const field = `${uiKey}_comment`;
				formik?.setFieldValue?.(field, "", false);
				formik?.setFieldTouched?.(field, false, false);
				setDismissedComments((d) => ({ ...(d || {}), [uiKey]: true }));
			} else {
				// open: if previously dismissed, ensure it's empty so user can type
				if (dismissedComments?.[uiKey]) {
					const field = `${uiKey}_comment`;
					formik?.setFieldValue?.(field, "", false);
					formik?.setFieldTouched?.(field, false, false);
				}
				setDismissedComments((d) => ({ ...(d || {}), [uiKey]: false }));
			}
			
			return { ...(prev || {}), [uiKey]: nextOpen };
		});
	}, [formik, dismissedComments]);

	/**
	 * Apply a RuleId to a given uiKey:
	 *   RuleId = 1 → both attachment and comment required  → open comment if AddComment
	 *   RuleId = 2 → only attachment required              → leave comment unchanged
	 *   RuleId = 3 → only comment required                 → open comment if AddComment
	 *   RuleId = null/undefined/0 → clear rule
	 *
	 * @param {string} uiKey        - The field-level UI key
	 * @param {number|null} ruleId  - 1, 2, 3 or null/0 to clear
	 * @param {boolean} addComment  - Whether the field has AddComment enabled
	 */
	const applyRuleForUiKey = useCallback((uiKey, ruleId, addComment) => {
		if (!uiKey) return;

		setActiveRules((prev) => {
			const next = { ...(prev || {}) };
			if (!ruleId) {
				delete next[uiKey];
			} else {
				next[uiKey] = Number(ruleId);
			}
			return next;
		});

		// Auto-open comment when rule requires it (ruleId 1 or 3) and AddComment is enabled
		const commentRequired = ruleId === 1 || ruleId === 3;
		if (addComment && commentRequired) {
			setOpenComments((prev) => ({ ...(prev || {}), [uiKey]: true }));
			setDismissedComments((d) => ({ ...(d || {}), [uiKey]: false }));
		}
	}, []);

	// On data load, if backend has Item.Comment, seed it into Formik and open the comment UI.
	// This covers answered questionnaires where comment comes as `Items[i].Comment`.
	useEffect(() => {
		if (!formik) return;
		const items = Array.isArray(data?.Items) ? data.Items : [];
		if (!items.length) return;
		
		const normalize = (v) => (v == null ? "" : String(v).trim());
		
		let didOpen = false;
		const nextOpen = {};
		
		items.forEach((raw) => {
			const backendComment = normalize(raw?.Comment);
			if (!backendComment) return;
			
			const node = {
				...raw,
				id: raw?.DetailId != null ? String(raw.DetailId) : String(raw?.id),
			};
			const uiKey = questionnaireFieldKey(node);
			if (!uiKey) return;
			
			// If user dismissed this comment, don't seed/open again.
			if (dismissedComments?.[uiKey]) return;
			
			const field = `${uiKey}_comment`;
			const cur = normalize(formik.values?.[field]);
			if (!cur) {
				formik.setFieldValue(field, backendComment, false);
			}
			
			nextOpen[uiKey] = true;
			didOpen = true;
		});
		
		if (didOpen) {
			setOpenComments((prev) => ({ ...(prev || {}), ...nextOpen }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.Items, formik, dismissedComments]);
	
	// For clones/attribute fields we generate stable unique ids.
	const makeUUID = useCallback(() => {
		// RFC4122 v4-ish (enough for UI ids); avoid extra deps
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}, []);
	
	// Build a stable id mapping for backend rows.
	const baseUiItems = useMemo(() => {
		const src = Array.isArray(data?.Items) ? data.Items : [];
		
		// 1) assign deterministic id per item (without changing property name)
		const nodes = src.map((it) => {
			return {
				...it,
				// UI id:
				// - answered: DetailId
				// - non-answered: backend id as-is
				id: it?.DetailId != null ? String(it.DetailId) : String(it?.id),
				_runtimeId: it?.DetailId != null ? `detail:${String(it.DetailId)}` : `${String(it?.Type ?? 'node')}:${String(it?.id)}`,
				// keep backend's item id for submit
				ItemId: it?.id,
			};
		});
		
		// 2) compute UI ParentId without renaming:
		// - answered: backend ParentId points to DetailId
		// - non-answered: backend ParentId points to backend item id
		const byDetail = new Map();
		nodes.forEach((n) => {
			if (n?.DetailId != null) byDetail.set(String(n.DetailId), n);
		});
		
		const byBackendId = new Map();
		nodes.forEach((n) => {
			const bid = n?.ItemId;
			if (bid == null) return;
			const key = String(bid);
			if (!byBackendId.has(key)) byBackendId.set(key, []);
			byBackendId.get(key).push(n);
		});
		
		nodes.forEach((n) => {
			// preserve original backend parent id for submit
			n.backendParentId = n?.ParentId ?? null;
			
			if (n.backendParentId == null || n.backendParentId === '') {
				n.ParentId = null;
				return;
			}
			
			// answered: backend ParentId points to a DetailId
			if (n?.DetailId != null || n?.Type === 'attribute') {
				const p = byDetail.get(String(n.backendParentId));
				n.ParentId = p?.id ?? null;
				return;
			}
			
			// non-answered: backend ParentId points to backend item id
			const list = byBackendId.get(String(n.backendParentId)) || [];
			const p = list[0];
			n.ParentId = p?.id ?? null;
		});
		
		return nodes;
	}, [data?.Items]);
	
	const mergedItems = useMemo(() => {
		const extraItems = Array.isArray(dynamicItems) ? dynamicItems : [];
		// IMPORTANT: keep clones in ONE place.
		// We still append clones into `data.Items` for submit, but we must NOT render them twice.
		// `baseUiItems` is derived from `data.Items` and may already include clones.
		const baseIds = new Set((baseUiItems || []).map((x) => String(x?.id)));
		const dedupedExtra = (extraItems || []).filter((x) => x && !baseIds.has(String(x.id)));
		return [...baseUiItems, ...dedupedExtra];
	}, [baseUiItems, dynamicItems]);
	
	const rootNodes = useMemo(() => {
		return buildTree(mergedItems);
	}, [mergedItems]);
	
	// useEffect(() => {
	// 	geoStart.getLocationAndAddress();
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, []);
	// const isGeoReady = !!geoStart.coords?.lat;
	const isGeoReady = true;
	
	
	const isFinalized = data?.Finalization === 1 || data?.Finalization === "1";
	// κάθε 5 λεπτά να κάνει προσωρινή αποθήκευση της φόρμας
	useEffect(() => {
		if (!isGeoReady || isFinalized) return;
		
		const interval = setInterval(async () => {
			await formik.setFieldValue("finalization", false, false);
			await formik.submitForm();
		}, 300000); // 5 λεπτά σε milliseconds
		
		return () => clearInterval(interval);
	}, [isGeoReady]);
	
	// Ensure touched structure matches values shape (Formik expects arrays for repeatable fields)
	const touchAllFields = useCallback((errorsArg) => {
		const values = formik.values || {};
		const nextTouched = Object.keys(values).reduce((acc, key) => {
			const v = values?.[key];
			// For attachment fields (key ends with _attachments): always mark as scalar true
			// because they are arrays but Formik touches them as a scalar boolean.
			if (key.endsWith('_attachments')) {
				acc[key] = true;
			} else if (Array.isArray(v)) {
				// Non-empty arrays: mark each index touched
				// Empty arrays: mark as scalar true so the required-array rule can fire
				acc[key] = v.length > 0 ? v.map(() => true) : true;
			} else {
				acc[key] = true;
			}
			return acc;
		}, {});
		
		// Also touch error-only keys (e.g. q:94) that may not exist in values yet.
		const errs = errorsArg || formik.errors || {};
		Object.keys(errs).forEach((k) => {
			if (k == null || k === '') return;
			if (nextTouched[k] !== undefined) return;
			// If error is array, mark each index touched so per-index messages render.
			if (Array.isArray(errs[k])) nextTouched[k] = errs[k].map(() => true);
			else nextTouched[k] = true;
		});
		// shouldValidate=true so UI + submit state are consistent after a click.
		formik.setTouched(nextTouched, true);
	}, [formik]);
	
	// Repeatable group helpers (must be defined before renderNode)
	const isRepeatableGroup = useCallback((node) => {
		return node?.Type === 'group' && (node?.Repeatable === '1' || node?.Repeatable === 1 || node?.Repeatable === true);
	}, []);
	
	const clearFormikValueForNode = useCallback((node) => {
		const fk = questionnaireFieldKey(node);
		if (!fk) return;
		// Only initialize/clear values for clones.
		// Clearing base node values is what was wiping answers when cloning.
		if (!node?._isClone) return;
		const typeId = Number(node?.TypeId);
		const isRepeatableField = node?.Repeatable === '1' || node?.Repeatable === 1 || node?.Repeatable === true;
		let init;
		if (isRepeatableField) init = [""];
		else if (typeId === 6 || typeId === 9) init = [];
		else init = "";
		// Each clone now has a unique u:<uiId> key, so it is always safe to initialize.
		formik?.setFieldValue?.(fk, init, false);
	}, [formik]);
	
	const cloneGroupSubtreeToFlatItems = useCallback((groupNode) => {
		if (!groupNode) return [];
		const newIdForOld = new Map();
		const makeNewId = () => `rgc:${makeUUID()}`;
		const makeUiId = () => `uic:${makeUUID()}`;
		const original = [];
		const visit = (n) => {
			if (!n) return;
			if (n.Type === 'attribute' || n._isAttributeField) return;
			// snapshot (avoid shared children refs)
			const snap = { ...n };
			delete snap.children;
			original.push(snap);
			(n.children || []).forEach(visit);
		};
		visit(groupNode);
		original.forEach((n) => newIdForOld.set(String(n.id), makeNewId()));
		return original.map((n) => {
			const newId = newIdForOld.get(String(n.id));
			const oldParent = n.ParentId;
			const newParentId = oldParent != null && oldParent !== '' ? (newIdForOld.get(String(oldParent)) ?? oldParent) : null;
			const templateId = String(n?._cloneOfId ?? n.id);
			const out = {
				...n,
				id: String(newId),
				_runtimeId: `clone:${String(newId)}`,
				// unique Formik identity for clones (questionnaireFieldKey prefers uiId)
				uiId: makeUiId(),
				ParentId: newParentId != null ? String(newParentId) : null,
				_isClone: true,
				_cloneOfId: templateId,
				DetailId: null,
				// IMPORTANT: Clear backendParentId on clones — they are new rows with no backend identity.
				// payloadParentIdFor must resolve their ParentId through the UI tree (ensureDetailId),
				// NOT through backendParentId which would point to the original template's backend row.
				backendParentId: null,
				children: [],
			};
			delete out.Value;
			delete out.value;
			delete out.Answer;
			delete out.answer;
			delete out.Attributes;
			return out;
		});
	}, [makeUUID]);
	
	const handleAddGroup = useCallback((groupNode) => {
		const clones = cloneGroupSubtreeToFlatItems(groupNode);
		if (!clones.length) return;
		setDynamicItems((prev) => [...(prev || []), ...clones]);
		// IMPORTANT: do NOT mutate data.Items.
		// data.Items drives Formik initialValues + enableReinitialize in usePreviewForm.
		// Mutating it causes Formik to reinitialize and wipe base group answers.
		clones.forEach((n) => {
			if (n?.Type === 'group') {
				const titleKey = `groupTitle:${questionnaireFieldKey(n)}`;
				// initialize clone title once; don't overwrite user edits
				if (!(formik?.values && Object.prototype.hasOwnProperty.call(formik.values, titleKey))) {
					formik?.setFieldValue?.(titleKey, n.Description ?? "", false);
				}
				return;
			}
			clearFormikValueForNode(n);
			// New clone fields should start untouched, otherwise UI can show required immediately.
			try {
				const fk = questionnaireFieldKey(n);
				if (fk) formik?.setFieldTouched?.(fk, false, false);
			} catch (e) {
				// ignore
			}
		});
	}, [cloneGroupSubtreeToFlatItems, clearFormikValueForNode, formik]);
	
	const handleRemoveGroupInstance = useCallback((groupNode, groupIdToRemove) => {
		const gid = String(groupIdToRemove);
		setDynamicItems((prev) => {
			const items = prev || [];
			const toDelete = new Set();
			const stack = [gid];
			while (stack.length) {
				const id = stack.pop();
				if (!id || toDelete.has(id)) continue;
				toDelete.add(id);
				items
				.filter((x) => x && String(x.ParentId) === String(id))
				.forEach((child) => stack.push(String(child.id)));
			}

			// Clear Formik values for all removed clone question/group nodes
			// so stale instance-scoped keys (u:<uiId>) don't persist in values or payload.
			items.forEach((x) => {
				if (!x) return;
				const shouldRemove = x._isAttributeField || x.Type === 'attribute'
					? toDelete.has(String(x.ParentId))
					: toDelete.has(String(x.id));
				if (!shouldRemove) return;
				try {
					const fk = questionnaireFieldKey(x);
					if (fk) {
						formik?.setFieldValue?.(fk, undefined, false);
						formik?.setFieldTouched?.(fk, false, false);
					}
				} catch (e) { /* ignore */ }
			});

			return items.filter((x) => {
				if (!x) return false;
				if (x._isAttributeField || x.Type === 'attribute') return !toDelete.has(String(x.ParentId));
				return !toDelete.has(String(x.id));
			});
		});
		// IMPORTANT: do NOT mutate data.Items.
		try {
			const fakeNode = { ...groupNode, id: gid, DetailId: null, _runtimeId: null };
			const titleKey = `groupTitle:${questionnaireFieldKey(fakeNode)}`;
			formik?.setFieldValue?.(titleKey, undefined, false);
		} catch (e) {
			// ignore
		}
	}, [formik]);
	
	const containerSx = {
		margin: "0 auto",
		p: 2,
		width: {
			xs: "100%",
			sm: "90%",
			md: "80%",
		},
	};
	
	if (loading || !formik || !data?.Items) return <SkeletonLoaderWrapper loading={loading}/>;
	
	// Error text: κόκκινο όταν υπάρχει
	const helperErrorSx = {
		color: "error.main",
		fontSize: 12,
	};
	
	const inputSx = {
		width: '95%',
		backgroundColor: "#f4f5fa",
		borderRadius: 3,
		"& .MuiInputBase-input": { fontSize: 13, padding: "10px 12px" },
		
		// Default border
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "#fff",
		},
		
		// Hover state
		"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "#fff",
		},
		
		// Focused state
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#fff",
			// boxShadow: "0 0 0 4px rgba(28,100,242,0.06)",
		},
	};
	/* ----HasAttributes function after select onChange----*/
	const handleSelectWithAttributesChange = (node, value) => {
		// Always target the CURRENT question UI node id.
		const parentId = String(node?.id ?? "");
		if (!parentId) return;
		
		// Clear Formik values for any existing attributes of this question (answered or dynamic)
		// so removed attribute fields never get submitted.
		try {
			const allItemsForCleanup = [
				...((Array.isArray(data?.Items) ? data.Items : [])),
				...((Array.isArray(dynamicItems) ? dynamicItems : [])),
			];
			const attrNodes = allItemsForCleanup.filter(
				(it) => it && it.Type === 'attribute' && String(it.ParentId) === parentId
			);
			attrNodes.forEach((attr) => {
				const fk = questionnaireFieldKey(attr);
				if (fk && formik?.values && Object.prototype.hasOwnProperty.call(formik.values, fk)) {
					formik.setFieldValue(fk, undefined, false);
					formik.setFieldTouched(fk, false, false);
				}
			});
		} catch (e) {
			// ignore cleanup errors
		}
		
		// 1) Always remove any existing attributes for this question.
		// IMPORTANT:
		// - In non-answered questionnaires attributes live in `dynamicItems`.
		// - In answered questionnaires attributes come from backend and exist inside `data.Items` (source of truth),
		//   so we must remove them from `data.Items` (NOT by mutating node.children which is derived).
		setDynamicItems((prev) => (prev || []).filter((it) => !(it?.Type === 'attribute' && String(it.ParentId) === parentId)));
		// IMPORTANT: in answered questionnaires, attributes come from backend (data.Items) and MUST remain there.
		// Otherwise they will never reach submit payload.
		if (!headerId) {
			setData?.((prev) => {
				if (!prev?.Items) return prev;
				const before = prev.Items.length;
				const nextItems = prev.Items.filter((it) => !(it?.Type === 'attribute' && String(it.ParentId) === parentId));
				return nextItems.length === before ? prev : { ...prev, Items: nextItems };
			});
		}
		
		// If value cleared => we've already removed attributes, nothing else to do.
		if (value == null || value === "") return;
		
		const categId = Number(value);
		if (!categId) return;
		
		const parentBackendId = String(node?.DetailId ?? node?.ItemId ?? "") || null;
		
		console.log('[ATTR] request', { parentId, nodeId: node?.ItemId ?? node?.id, categId, parentBackendId });
		
		axios
		.post(`${API}/en/attributes/item/values`, { CategId: categId })
		.then((res) => {
			const newJson = res?.data?.data;
			const newItems = transformAttributeJson({
				attributes: newJson,
				parentId,
				questionnaireId: data.id,
				parentBackendId,
			});
			
			// Ensure old ones are gone (idempotent) and insert only the new ones
			setDynamicItems((prev) => {
				const withoutParent = (prev || []).filter(
					(it) => !(it?.Type === 'attribute' && String(it.ParentId) === parentId)
				);
				return [...withoutParent, ...newItems];
			});
			
			newItems.forEach((it) => {
				const fk = questionnaireFieldKey(it);
				if (!fk) return;
				if (formik?.values?.[fk] === undefined) {
					const typeId = Number(it?.TypeId);
					const init = (typeId === 6 || typeId === 9) ? [] : "";
					formik.setFieldValue(fk, init, false);
				}
			});
		})
		.catch((e) => console.log("[ATTR] api error", e));
	};
	
	const transformAttributeJson = ({ attributes, parentId, questionnaireId, parentBackendId }) => {
		if (!Array.isArray(attributes)) return [];
		
		return attributes.flatMap((attr) =>
			(attr.details || []).map((d, index) => {
				const options = (d.ListValues || []).map((opt) => ({
					id: opt.id,
					Description: opt.Description,
					isDefault: opt.isDefault,
					OrderNum: opt.OrderNum,
					isChecked: opt.isChecked,
					isOther: opt.isOther,
				}));
				
				// Attribute nodes must NEVER share React keys / DOM ids with question nodes.
				// We keep ParentId pristine (raw question node id), but we namespace the attribute node id.
				const attrId = String(d.id);
				const id = `attr:${String(parentId)}:${attrId}`;
				
				return {
					id,
					_runtimeId: `attr:${String(parentId)}:${attrId}`,
					ParentId: String(parentId),
					DetailId: null,
					AttributeId: attrId,
					backendParentId: parentBackendId,
					QuestionnaireId: questionnaireId,
					Order: d.OrderNum ?? index,
					Type: "attribute",
					TypeId: d.TypeId,
					Description: d.Description || "",
					useQuery: "0",
					Query: null,
					Placeholder: "",
					ErrorMessage: "",
					InfoMessage: d.Comments || "",
					Required: null,
					Repeatable: null,
					HasAttributes: null,
					AddComment: "0",
					AddAttachment: "0",
					Readonly: null,
					isHidden: null,
					isComplex: null,
					Regex: d.RegularExpr || "",
					RegexMessage: "",
					QueryColumnName: "",
					Options: options,
					Min: null,
					Max: null,
					Step: null,
					Decimals: null,
					ListId: d.ListId || null,
					UsePredefinedList: options.length > 0 ? "1" : "0",
					children: [],
					_isAttributeField: true,
				};
			})
		);
	};
	
	/* --- Repeatable field helpers --- */
	const handleAddRepeatable = (fieldId) => {
		const cur = formik.values?.[fieldId];
		const arr = Array.isArray(cur) ? cur : (cur == null ? [] : [cur]);
		const next = [...arr, ""];
		formik.setFieldValue(fieldId, next, true);
		// keep touched shape in sync so errors can render per index after validate
		const curTouched = formik.touched?.[fieldId];
		if (Array.isArray(curTouched)) formik.setFieldTouched(fieldId, [...curTouched, false], false);
	};
	
	const handleRemoveRepeatable = (fieldId, index) => {
		const cur = formik.values?.[fieldId];
		const arr = Array.isArray(cur) ? cur : (cur == null ? [] : [cur]);
		const next = arr.filter((_, i) => i !== index);
		formik.setFieldValue(fieldId, next.length > 0 ? next : [""], true);
		// remove touched + errors for this index to avoid sticky array validation
		const tcur = formik.touched?.[fieldId];
		if (Array.isArray(tcur)) {
			const tnext = tcur.filter((_, i) => i !== index);
			formik.setFieldTouched(fieldId, tnext, false);
		}
		const ecur = formik.errors?.[fieldId];
		if (Array.isArray(ecur)) {
			const enext = ecur.filter((_, i) => i !== index);
			formik.setFieldError(fieldId, enext);
		}
	};
	
	
	const makeFieldKey = (node) => questionnaireFieldKey(node);
	
	const renderField = (node) => {
		// For repeatables, use aggregate key for values/errors, but keep instance key for UI-only controls.
		const isRepeatableNode = toBool(node?.Repeatable);

		// IMPORTANT:
		// Every cloned question inside a repeatable group has a unique uiId (uic:<uuid>).
		// makeFieldKey(node) returns u:<uiId> for those nodes — fully instance-scoped.
		// We deliberately do NOT use a shared q:<ItemId> aggregate key for clones, because that
		// was the root cause of all group instances sharing the same Formik value.
		const valueKey = String(node?.Type) === 'attribute'
			? getAttributeFieldKey(node)
			: (isRepeatableNode ? getAggregateQuestionKey(node) : makeFieldKey(node));
		const uiKey = isRepeatableNode ? getInstanceUiKey(node) : valueKey;
		const backendAttachmentsFiltered = (() => {
			const names = normalizeBackendAttachments(node?.Attachments)
			.map(attachmentToName)
			.filter(Boolean);
			const removed = removedBackendAttachments?.[uiKey] || [];
			if (!Array.isArray(removed) || removed.length === 0) return names;
			return names.filter((n) => !removed.includes(n));
		})();
		const canDeleteBackendAttachments = !isFinalized;
		const removeBackendAttachment = (fileName) => {
			if (!fileName) return;
			setRemovedBackendAttachments((prev) => {
				const cur = (prev && prev[uiKey]) ? prev[uiKey] : [];
				if (cur.includes(fileName)) return prev;
				return { ...(prev || {}), [uiKey]: [...cur, fileName] };
			});
		};
		
		const fieldKey = valueKey;
		const type = Number(node.TypeId);
		const value = formik.values?.[valueKey];
		const commonProps = { name: String(fieldKey), onBlur: formik.handleBlur };
		
		const isReadonly = node.Readonly === "1" || isFinalized;
		
		/* --- Repeatable fields --- */
		if (isRepeatableNode) {
			// Stable shape for repeatable values.
			// IMPORTANT: never call setFieldValue during render; it can create validation loops / sticky errors.
			const arr = Array.isArray(value) && value?.length > 0 ? value : [value ?? ""];
			return (
				<Box>
					<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
						{node.Description}
						{node.InfoMessage &&
							<Tooltip title={
								<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
								  {node.InfoMessage}
								</span>}
							>
								<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
									<InfoOutlined fontSize="small"/>
								</IconButton>
							</Tooltip>
						}
					</InputLabel>
					{arr.map((val, idx) => {
						const indexedName = `${String(valueKey)}[${idx}]`;
						return (
							<Stack key={`${fieldKey}:${idx}`} direction="column" spacing={0.5}
								   sx={{ width: "100%", mb: 1 }}>
								<Stack direction="row" spacing={1} alignItems="center">
									{type === 1 && (
										<TextField
											{...commonProps}
											name={indexedName}
											onBlur={formik.handleBlur}
											size="small"
											fullWidth
											value={val ?? ""}
											onChange={(e) => {
												const updated = [...arr];
												updated[idx] = e.target.value;
												formik.setFieldValue(valueKey, updated, true);
											}}
											placeholder={node.Placeholder || ""}
											sx={inputSx}
											slotProps={{ readOnly: isReadonly }}
										/>
									)}
									{type === 2 && (
										<NumericFormat
											{...commonProps}
											name={indexedName}
											onBlur={formik.handleBlur}
											customInput={TextField}
											fullWidth
											size="small"
											value={val}
											onValueChange={(vals) => {
												const updated = [...arr];
												updated[idx] = vals.floatValue ?? "";
												formik.setFieldValue(valueKey, updated, true);
											}}
											placeholder={node.Placeholder || ""}
											sx={inputSx}
											isAllowed={() => true}
											slotProps={{ readOnly: isReadonly }}
										/>
									)}
									{type === 3 && (
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DatePicker
												ampm={false}
												value={val || null}
												format={`${dateFormat}`}
												onChange={(newValue) => {
													const updated = [...arr];
													updated[idx] = newValue;
													formik.setFieldValue(valueKey, updated, true);
												}}
												placeholder={node.Placeholder || ""}
												slotProps={{
													textField: {
														name: indexedName,
														onBlur: formik.handleBlur,
														size: "small",
														fullWidth: true,
														sx: inputSx,
														readOnly: isReadonly,
													},
												}}
											/>
										</LocalizationProvider>
									)}
									{type === 10 && (
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<TimePicker
												value={val || null}
												format={`${timeFormat}`}
												onChange={(newValue) => {
													const updated = [...arr];
													updated[idx] = newValue;
													formik.setFieldValue(valueKey, updated, true);
												}}
												slotProps={{
													textField: {
														name: indexedName,
														onBlur: formik.handleBlur,
														size: "small",
														fullWidth: true,
														sx: inputSx,
														readOnly: isReadonly,
													},
												}}
											/>
										</LocalizationProvider>
									)}
									{type === 11 && (
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DateTimePicker
												value={val || null}
												format={`${dateFormat} ${timeFormat}`}
												onChange={(newValue) => {
													const updated = [...arr];
													updated[idx] = newValue;
													formik.setFieldValue(valueKey, updated, true);
												}}
												slotProps={{
													textField: {
														name: indexedName,
														onBlur: formik.handleBlur,
														size: "small",
														fullWidth: true,
														sx: inputSx,
														readOnly: isReadonly,
													},
												}}
											/>
										</LocalizationProvider>
									)}
									{type === 4 && (
										<TextField
											select
											fullWidth
											size="small"
											name={indexedName}
											onBlur={formik.handleBlur}
											value={val ?? ""}
											onChange={(e) => {
												const newVal = e.target.value;
												const updated = [...arr];
												updated[idx] = newVal;
												formik.setFieldValue(valueKey, updated, true);

												// RuleId handling (field-level: any selected value with a RuleId applies to whole field)
												const selectedOpt = Array.isArray(node?.Options)
													? node.Options.find((o) => String(o?.id) === String(newVal))
													: null;
												const ruleId = selectedOpt?.RuleId ? Number(selectedOpt.RuleId) : null;
												// Check if any OTHER index still has a ruleId active
												const otherRuleId = ruleId ? ruleId : (() => {
													const remainingValues = updated.filter((v, i) => i !== idx);
													for (const rv of remainingValues) {
														const opt = Array.isArray(node?.Options)
															? node.Options.find((o) => String(o?.id) === String(rv))
															: null;
														if (opt?.RuleId) return Number(opt.RuleId);
													}
													return null;
												})();
												applyRuleForUiKey(uiKey, otherRuleId, toBool(node.AddComment));

												if (node.HasAttributes) {
													handleSelectWithAttributesChange(node, newVal);
												}
											}}
											sx={inputSx}
											slotProps={{ readOnly: isReadonly }}
										>
											{node.Placeholder &&
												<MenuItem value="" disabled>
													{node.Placeholder}
												</MenuItem>
											}
											{node.Options?.map((opt) => (
												<MenuItem key={opt.id} value={opt.id}>
													{opt.Description}
												</MenuItem>
											))}
										</TextField>
									)}
									{/* Add / Remove buttons */}
									{!isFinalized && (
										<IconButton size="small" color="error"
													onClick={() => handleRemoveRepeatable(valueKey, idx)}>
											<ClearIcon/>
										</IconButton>
									)}
									{idx === arr.length - 1 && !isFinalized && (
										<IconButton size="small" color="primary"
													onClick={() => handleAddRepeatable(valueKey)}>
											<AddIcon/>
										</IconButton>
									)}
								</Stack>
								<FormHelperText
									sx={helperErrorSx}>{getFieldError(formik, valueKey, idx)}</FormHelperText>
							</Stack>
						);
					})}
					{/*comment & attachments*/}
					{(() => {
						const activeRule = activeRules[uiKey] ?? null;
						// RuleId=1 → both required; RuleId=2 → attachment required; RuleId=3 → comment required
						const commentRequired = activeRule === 1 || activeRule === 3;
						const attachmentRequired = activeRule === 1 || activeRule === 2;
						// Force-show comment field when required by rule and field supports it
						const showComment = openComments[uiKey] || (commentRequired && toBool(node.AddComment));
						// Force-show attachment when required (AddAttachment may be off — only show if AddAttachment enabled)
						const showAttachment = toBool(node.AddAttachment) || attachmentRequired;

						const attachmentsKey = `${uiKey}_attachments`;
						const commentKey = `${uiKey}_comment`;
						const attachmentsTouched = formik.touched?.[attachmentsKey] === true;
						const attachmentsEmpty = (formik.values?.[attachmentsKey] || []).length === 0;
						const showAttachmentError = attachmentRequired && attachmentsTouched && attachmentsEmpty;
						const commentError = getFieldError(formik, commentKey);

						return (
							<Box sx={{ width: "100%" }}>
								{showComment && (
									<TextField
										fullWidth
										size="small"
										multiline
										rows={2}
										name={commentKey}
										value={formik.values[commentKey] || ""}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										placeholder={t("Add comment")}
										sx={inputSx}
										slotProps={{ input: { readOnly: false } }}
										error={commentRequired && Boolean(commentError)}
									/>
								)}
								{commentRequired && Boolean(commentError) && (
									<FormHelperText sx={helperErrorSx}>{commentError}</FormHelperText>
								)}
								{!isFinalized && (
									<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
										{toBool(node.AddComment) && (
											<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
												{openComments[uiKey] ? <ClearIcon fontSize="small"/> : <ModeCommentOutlined fontSize="small"/>}
											</IconButton>
										)}
										{showAttachment && (
											<FileUpload name={attachmentsKey} formik={formik} />
										)}
									</Box>
								)}
								{showAttachmentError && (
									<FormHelperText sx={helperErrorSx}>{t("Required Attachment")}</FormHelperText>
								)}
								{/* backend attachments */}
								{toBool(node.AddAttachment) && (
									<BackendAttachmentsLinks
										attachments={backendAttachmentsFiltered}
										canDelete={canDeleteBackendAttachments}
										onDelete={removeBackendAttachment}
									/>
								)}
							</Box>
						);
					})()}
					{node.children?.length > 0 && (
						<Grid size={12} sx={{paddingBottom:'5px'}}>
							<Box sx={{ mt: 0, ml: 3 }}>
								{node.children.map((child) => renderNode(child))}
							</Box>
						</Grid>
					)}
				</Box>
			);
		}
		
		/* --- Non-repeatable fields --- */
		switch (type) {
			case 1: // Text
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						
						</InputLabel>
						<TextField
							{...commonProps}
							fullWidth
							size="small"
							value={value ?? ""}
							onChange={formik.handleChange}
							placeholder={node.Placeholder || ""}
							sx={inputSx}
							slotProps={{ readOnly: isReadonly }}
						/>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			
			case 2: // Number
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						
						</InputLabel>
						<NumericFormat
							value={value ?? ""}
							onValueChange={(vals) => formik.setFieldValue(fieldKey, vals.floatValue ?? "")}
							customInput={TextField}
							fullWidth
							size="small"
							name={String(fieldKey)}
							onBlur={formik.handleBlur}
							placeholder={node.Placeholder || ""}
							sx={inputSx}
							isAllowed={() => true}
							InputProps={{ readOnly: isReadonly }}
						/>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			
			case 3: // Date
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								ampm={false}
								value={value || null}
								format={`${dateFormat}`}
								onChange={(newValue) => formik.setFieldValue(fieldKey, newValue)}
								placeholder={node.Placeholder || ""}
								slotProps={{
									textField: {
										size: 'small',
										fullWidth: true,
										sx: inputSx,
										readOnly: isReadonly,
										name: String(fieldKey),
									},
								}}
							/>
						</LocalizationProvider>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			
			case 4: // Select
				// AddNewValue helpers (keep logic out of JSX)
				const getAllowNewValue = (selectedValue) => {
					if (!Array.isArray(node?.Options)) return false;
					const selectedOpt = node.Options.find((o) => String(o?.id) === String(selectedValue));
					return toBool(selectedOpt?.isOther);
				};
				
				const renderNewValueField = () => {
					const allowNewValue = getAllowNewValue(value);
					if (!allowNewValue) return null;
					
					const newValueKey = `${uiKey}_newValue`;
					return (
						<Box sx={{ mt: 1 }}>
							<TextField
								fullWidth
								size="small"
								name={newValueKey}
								value={formik.values?.[newValueKey] || ""}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								placeholder={t("New value")}
								sx={inputSx}
								slotProps={{ readOnly: isReadonly }}
							/>
						</Box>
					);
				};
				
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						
						</InputLabel>
						<TextField
							select
							fullWidth
							size="small"
							{...commonProps}
							onChange={(e) => {
								const newValue = e.target.value;
								formik.setFieldValue(fieldKey, newValue);
								
								// AddNewValue support: clear NewValue when selected option doesn't allow it
								const newValueKey = `${uiKey}_newValue`;
								const allowNewValue = getAllowNewValue(newValue);
								if (!allowNewValue) {
									formik.setFieldValue(newValueKey, "", false);
									formik.setFieldTouched(newValueKey, false, false);
								}

								// RuleId handling
								const selectedOpt = Array.isArray(node?.Options)
									? node.Options.find((o) => String(o?.id) === String(newValue))
									: null;
								const ruleId = selectedOpt?.RuleId ? Number(selectedOpt.RuleId) : null;
								applyRuleForUiKey(uiKey, ruleId, toBool(node.AddComment));

								if (node.HasAttributes) {
									handleSelectWithAttributesChange(node, newValue);
								}
							}}
							sx={inputSx}
							value={value ?? ""}
							slotProps={{ readOnly: isReadonly }}
						>
							{node.Placeholder &&
								<MenuItem value="" disabled>
									{node.Placeholder}
								</MenuItem>
							}
							
							{Array.isArray(node.Options) && node.Options.length > 0 ? (
								node.Options.map(opt => (
									<MenuItem key={opt.id} value={opt.id}>
										{opt.Description}
									</MenuItem>
								))
							) : (
								<MenuItem disabled>{t('No options')}</MenuItem>
							)}
						</TextField>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>

						{renderNewValueField()}

						{(() => {
							const activeRule = activeRules[uiKey] ?? null;
							const commentRequired = activeRule === 1 || activeRule === 3;
							const attachmentRequired = activeRule === 1 || activeRule === 2;
							const showComment = openComments[uiKey] || (commentRequired && toBool(node.AddComment));
							const showAttachment = toBool(node.AddAttachment) || attachmentRequired;

							const attachmentsKey = `${uiKey}_attachments`;
							const commentKey = `${uiKey}_comment`;
							const attachmentsTouched = formik.touched?.[attachmentsKey] === true;
							const attachmentsEmpty = (formik.values?.[attachmentsKey] || []).length === 0;
							const showAttachmentError = attachmentRequired && attachmentsTouched && attachmentsEmpty;
							const commentError = getFieldError(formik, commentKey);

							return (
								<Box sx={{ width: "100%" }}>
									{showComment && (
										<TextField
											fullWidth
											size="small"
											multiline
											rows={2}
											name={commentKey}
											value={formik.values[commentKey] || ""}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											placeholder={t("Add comment")}
											sx={inputSx}
											slotProps={{ input: { readOnly: false } }}
											error={commentRequired && Boolean(commentError)}
										/>
									)}
									{commentRequired && Boolean(commentError) && (
										<FormHelperText sx={helperErrorSx}>{commentError}</FormHelperText>
									)}
									{!isFinalized && (
										<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
											{toBool(node.AddComment) && (
												<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
													{openComments[uiKey] ? <ClearIcon fontSize="small"/> : <ModeCommentOutlined fontSize="small"/>}
												</IconButton>
											)}
											{showAttachment && (
												<FileUpload name={attachmentsKey} formik={formik} />
											)}
										</Box>
									)}
									{showAttachmentError && (
										<FormHelperText sx={helperErrorSx}>{t("Required Attachment")}</FormHelperText>
									)}
									{toBool(node.AddAttachment) && (
										<BackendAttachmentsLinks
											attachments={backendAttachmentsFiltered}
											canDelete={canDeleteBackendAttachments}
											onDelete={removeBackendAttachment}
										/>
									)}
								</Box>
							);
						})()}
						
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			
			case 5: // Radio
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						
						</InputLabel>
						<FormControl component="fieldset" error={Boolean(getFieldError(formik, fieldKey))}>
							{node.Options?.length > 0 ?
								<RadioGroup
									name={String(fieldKey)}
									value={value ?? ""}
									onChange={(e) => formik.setFieldValue(fieldKey, e.target.value)}
									sx={{
										flexDirection: node.Options?.length <= 3 ? 'row' : 'column',
										flexWrap: 'wrap',
										gap: 1,
									}}
									disabled={isReadonly}
								>
									
									{node.Options?.map((opt) => (
										<FormControlLabel
											key={opt.id}
											value={opt.id}
											control={<Radio size="small"/>}
											label={opt.Description}
											sx={{ color: "text.primary" }}
											placeholder={node.Placeholder || ""}
										/>
									))}
								</RadioGroup>
								:
								<Typography> {t('There are not options')} </Typography>
							}
							<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						</FormControl>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			
			case 6: // Checkbox
				return (
					<FormControl
						component="fieldset"
						error={Boolean(getFieldError(formik, fieldKey))}
					>
						<Stack spacing={0.5}>
							{node.Options?.length > 0 && (
								<Typography
									sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}
								>
									{node.Description}
									{node.InfoMessage && (
										<Tooltip title={
											<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
											  {node.InfoMessage}
											</span>
										}>
											<IconButton
												sx={{ padding: "0px !important", height: "auto" }}
											>
												<InfoOutlined fontSize="small"/>
											</IconButton>
										</Tooltip>
									)}
								</Typography>
							)}
							
							{node.Options?.length > 0 ? (
								node.Options.map((opt) => {
									const checked =
										Array.isArray(formik.values[fieldKey]) &&
										formik.values[fieldKey].includes(opt.id);
									
									return (
										<FormControlLabel
											key={opt.id}
											control={
												<Checkbox
													checked={checked}
													onChange={(e) => {
														const prev = formik.values[fieldKey] || [];
														if (e.target.checked) {
															formik.setFieldValue(fieldKey, [...prev, opt.id]);
														} else {
															formik.setFieldValue(
																fieldKey,
																prev.filter((v) => v !== opt.id)
															);
														}
													}}
													size="small"
												/>
											}
											label={opt.Description}
											sx={{ color: "text.primary", mr: 0 }}
											disabled={isReadonly}
										/>
									);
								})
							) : (
								<FormControlLabel
									control={
										<Checkbox
											checked={Boolean(formik.values[fieldKey])}
											onChange={(e) =>
												formik.setFieldValue(fieldKey, e.target.checked)
											}
											size="small"
										/>
									}
									label={node.Description}
									sx={{ color: "text.primary" }}
									disabled={isReadonly}
								/>
							)}
						</Stack>
						
						<FormHelperText sx={helperErrorSx}>
							{getFieldError(formik, fieldKey)}
						</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "flex-end",
										gap: 0.5,
										mb: 0.5,
									}}
								>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? (
												<ClearIcon fontSize="small"/>
											) : (
												<ModeCommentOutlined fontSize="small"/>
											)}
										</IconButton>
									)}
									
									{toBool(node.AddAttachment) && (
										<FileUpload
											name={`${uiKey}_attachments`}
											formik={formik}
										/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</FormControl>
				);
			
			case 7:
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage && (
								<Tooltip
									title={
										<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
											{node.InfoMessage}
										</span>
									}
								>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							)}
						</InputLabel>
						<Button
							variant="outlined"
							size="small"
							disabled={isReadonly}
							onClick={() => console.log("add", node.id)}
						>
							{t('Add')}
						</Button>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
					</>
				);
			
			case 8: // Τextarea
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						
						</InputLabel>
						<TextField
							{...commonProps}
							fullWidth
							size="small"
							multiline
							rows={2}
							value={value ?? ""}
							onChange={formik.handleChange}
							placeholder={node.Placeholder || ""}
							sx={inputSx}
							slotProps={{
								readOnly: isReadonly,
							}}
							// slotProps={{
							// 	input: {
							// 		readOnly: node.Readonly,
							// 	},
							// }}
						/>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "flex-end",
										gap: 0.5,
										mb: 0.5,
									}}
								>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? (
												<ClearIcon fontSize="small"/>
											) : (
												<ModeCommentOutlined fontSize="small"/>
											)}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload
											name={`${uiKey}_attachments`}
											formik={formik}
										/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				
				);
			case 9: // Multiselect
				// CustomAutocomplete expects value to be an array of option objects.
				// Answered attributes can arrive as scalar ids (e.g. "261"). We normalize:
				// - formik stores array of ids
				// - UI receives array of option objects for proper label rendering
			{
				const options = Array.isArray(node?.Options) ? node.Options : [];
				const ids = Array.isArray(value) ? value : (value ? [value] : []);
				const selectedOptions = ids
				.map((id) => options.find((o) => String(o?.id) === String(id)))
				.filter(Boolean);
				
				// Ensure the rendered DOM input id is unique.
				// MUI's Autocomplete/TextField can emit a duplicate `id` if we don't provide one,
				// especially when multiple fields share the same label/name in repeatables.
				const domId = `fa-${String(node?._runtimeId || `${node?.Type}:${node?.id}`)}`;
				
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
										  {node.InfoMessage}
										</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						</InputLabel>
						<CustomAutocomplete
							identifier="id"
							onChange={(evt, items) => {
								// items is an array of option objects
								const nextIds = Array.isArray(items) ? items.map((it) => it?.id).filter(Boolean) : [];
								formik.setFieldValue(fieldKey, nextIds);
							}}
							value={selectedOptions}
							options={options}
							labelKey={'Description'}
							otherprops={{
								multiple: true,
								disableCloseOnSelect: true,
							}}
							otherTextFieldProps={{
								id: domId,
								sx: {
									'& .MuiInputBase-root': {
										...inputSx,
									},
								},
							}}
							disabled={isReadonly}
						/>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ readOnly: false }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{/* Multiselect case: after the existing comment/upload box, add backend attachments */}
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				);
			}
			
			case 10: // Time
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								value={value || null}
								format={`${timeFormat}`}
								onChange={(newValue) => formik.setFieldValue(fieldKey, newValue)}
								slotProps={{
									textField: {
										size: 'small',
										fullWidth: true,
										sx: inputSx,
										readOnly: isReadonly,
										name: String(fieldKey),
									},
								}}
							/>
						</LocalizationProvider>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ input: { readOnly: false } }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				);

			case 11: // DateTime
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						</InputLabel>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								value={value || null}
								format={`${dateFormat} ${timeFormat}`}
								onChange={(newValue) => formik.setFieldValue(fieldKey, newValue)}
								slotProps={{
									textField: {
										size: 'small',
										fullWidth: true,
										sx: inputSx,
										readOnly: isReadonly,
										name: String(fieldKey),
									},
								}}
							/>
						</LocalizationProvider>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
						<Box sx={{ width: "100%" }}>
							{openComments[uiKey] && (
								<TextField
									fullWidth
									size="small"
									multiline
									rows={2}
									name={`${uiKey}_comment`}
									value={formik.values[`${uiKey}_comment`] || ""}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t("Add comment")}
									sx={inputSx}
									slotProps={{ input: { readOnly: false } }}
								/>
							)}
							{!isFinalized && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
									{toBool(node.AddComment) && (
										<IconButton size="small" onClick={() => toggleCommentVisibility(uiKey)}>
											{openComments[uiKey] ? <ClearIcon fontSize="small"/> :
												<ModeCommentOutlined fontSize="small"/>}
										</IconButton>
									)}
									{toBool(node.AddAttachment) && (
										<FileUpload name={`${uiKey}_attachments`} formik={formik}/>
									)}
								</Box>
							)}
						</Box>
						{toBool(node.AddAttachment) && (
							<BackendAttachmentsLinks
								attachments={backendAttachmentsFiltered}
								canDelete={canDeleteBackendAttachments}
								onDelete={removeBackendAttachment}
							/>
						)}
						{node.children?.length > 0 && (
							<Grid size={12} sx={{paddingBottom:'5px'}}>
								<Box sx={{ mt: 1, ml: 3 }}>
									{node.children.map(child => renderNode(child))}
								</Box>
							</Grid>
						)}
					</>
				);

			case 12: // Signature
				return (
					<>
						<InputLabel sx={{ fontSize: 12, color: "#485476", fontWeight: 600, mb: 0.5 }}>
							{node.Description}
							{node.InfoMessage &&
								<Tooltip title={
									<span style={{ fontSize: '12px', lineHeight: '1.5' }}>
									  {node.InfoMessage}
									</span>
								}>
									<IconButton sx={{ padding: '0px !important', height: 'auto' }}>
										<InfoOutlined fontSize="small"/>
									</IconButton>
								</Tooltip>
							}
						</InputLabel>
						<SignaturePad
							value={formik.values[fieldKey] || ""}
							onChange={(dataURL) => {
								formik.setFieldValue(fieldKey, dataURL, true);
								formik.setFieldTouched(fieldKey, true, false);
							}}
							readOnly={isReadonly || isFinalized}
						/>
						<FormHelperText sx={helperErrorSx}>{getFieldError(formik, fieldKey)}</FormHelperText>
					</>
				);

			default:
				return <Typography color="error">{t('Unknown type')}</Typography>;
		}
	};
	
	const renderNode = (node) => {
		if (node.isHidden === "1") return null;
		if (node.isActive === "0" || node.isActive === 0 || node.isActive === false) return null;
		
		const nodeReadonly = node.Readonly === "1" || node.Readonly === 1 || node.Readonly === true;
		const isNodeLocked = nodeReadonly || isFinalized;
		
		// --- Group Node ---
		if (node.Type === "group") {
			const isRepeatable = isRepeatableGroup(node);
			if (!isRepeatable) {
				const groupTitleKey = `groupTitle:${questionnaireFieldKey(node)}`;
				return (
					<Accordion
						key={node._runtimeId || String(node.id)}
						defaultExpanded
						sx={{
							width: '100%', borderRadius: '15px',
							marginBottom: '5px', backgroundColor: '#2aafdb12',
							opacity: isNodeLocked ? 0.75 : 1,
							'&&': { borderRadius: '15px', overflow: 'hidden' },
							'&:before': { display: 'none' },
							'&&:first-of-type': { borderTopLeftRadius: '15px', borderTopRightRadius: '15px' },
							'&&:last-of-type': { borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px' },
						}}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ display: "flex", alignItems: "center" }}>
							<TextField
								size="small"
								variant="standard"
								fullWidth
								value={formik.values?.[groupTitleKey] ?? node.Description ?? ""}
								onChange={(e) => formik.setFieldValue(groupTitleKey, e.target.value)}
								onBlur={formik.handleBlur}
								name={groupTitleKey}
								sx={{
									'& .MuiInputBase-input': {
										fontWeight: 700,
										fontSize: (theme) => theme.typography.h6.fontSize
									},
								}}
								slotProps={{ readOnly: isNodeLocked }}
							/>
						</AccordionSummary>
						<AccordionDetails>
							<Box>
								<Grid container spacing={2}>
									{(node.children || []).map((child) => (
										<Grid size={12} sx={{paddingBottom:'5px'}}  key={child._runtimeId || String(child.id)}>
											{renderNode(child)}
										</Grid>
									))}
								</Grid>
							</Box>
						</AccordionDetails>
					</Accordion>
				);
			}
			
			// Repeatable group instances
			const cloneGroups = (dynamicItems || [])
			.filter((it) => it && it.Type === 'group' && it._isClone && String(it._cloneOfId) === String(node.id));
			const instances = [node, ...cloneGroups].filter((g, index) => {
				if (index === 0) return true;
				return Array.isArray(g?.children) && g.children.length > 0;
			});
			
			const groupTitleKeyForInstance = (instanceNode) => `groupTitle:${questionnaireFieldKey(instanceNode)}`;
			
			return instances.map((instance, idx) => (
				<Accordion
					key={instance._runtimeId || String(instance.id) || `${node.id}_${idx}`}
					defaultExpanded
					sx={{
						width: '100%', borderRadius: '15px',
						marginBottom: '5px', backgroundColor: '#2aafdb12',
						opacity: isNodeLocked ? 0.75 : 1,
						'&&': { borderRadius: '15px', overflow: 'hidden' },
						'&:before': { display: 'none' },
						'&&:first-of-type': { borderTopLeftRadius: '15px', borderTopRightRadius: '15px' },
						'&&:last-of-type': { borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px' },
					}}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ display: "flex", alignItems: "center" }}>
						<TextField
							size="small"
							variant="standard"
							fullWidth
							value={formik.values?.[groupTitleKeyForInstance(instance)] ?? instance.Description ?? ""}
							onChange={(e) => formik.setFieldValue(groupTitleKeyForInstance(instance), e.target.value)}
							onBlur={formik.handleBlur}
							name={groupTitleKeyForInstance(instance)}
							sx={{
								'& .MuiInputBase-input': {
									fontWeight: 700,
									fontSize: (theme) => theme.typography.h6.fontSize
								},
							}}
							slotProps={{ readOnly: isNodeLocked }}
						/>
						{/* delete button for clones only */}
						{instance?._isClone && (
							<IconButton
								sx={{ ml: "auto" }}
								color="error"
								onClick={(e) => {
									e.stopPropagation();
									handleRemoveGroupInstance(node, instance.id);
								}}
							>
								<ClearIcon/>
							</IconButton>
						)}
						{/* add button only on last instance */}
						{idx === instances.length - 1 && (
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									handleAddGroup(node);
								}}
							>
								<AddIcon/>
							</IconButton>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Box>
							<Grid container spacing={2}>
								{(instance.children || []).map((child) => (
									<Grid size={12} sx={{paddingBottom:'5px'}} key={child._runtimeId || String(child.id)}>
										{renderNode(child)}
									</Grid>
								))}
							</Grid>
						</Box>
					</AccordionDetails>
				</Accordion>
			));
		}
		
		// --- Field Node (εκτός group) ---
		return (
			<Grid size={12} sx={{paddingBottom:'5px'}} key={node._runtimeId || String(node.id)}>
				<Box sx={{ backgroundColor: '#fff', borderRadius: '10px', p: 1, boxShadow: 1 }}>
					{renderField(node)}
				</Box>
			</Grid>
		);
	};
	
	return (
		<FormikProvider value={formik}>
			<form autoComplete="off" onSubmit={formik.handleSubmit}>
				{/*<SidePanelLinks code={'Questionnaire_Index'}/>*/}
				<Box sx={containerSx}>
					<Box sx={{ mb: 2 }}>
						<Typography variant="h5" sx={{ fontWeight: 800 }}>
							{data?.Description || data?.Title || "Form Preview"}
						</Typography>
					</Box>
					<Box sx={{ mb: 2 }}>
						<Typography>
							{data?.GlobalComment || data?.Comment}
						</Typography>
					</Box>

					<Grid container spacing={2}>
						{rootNodes.map((root) => renderNode(root))}
					</Grid>

					{/*<Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: '5px' }}>*/}
					{!onClose && //το onClose υπάρχει μονο αν ανοιγει απο modal. Οπότε έχουν μπει έλεγχοι για να εμφανίζονται τα κατάλληλα buttons αναλογα με το απο που ανοίγουν.
						(
							<Box sx={{ display: 'flex', gap: 1, justifyContent: 'end', paddingTop: '15px' }}>
								<Button
									type="button"
									variant="contained"
									onClick={async () => {
										console.log('[UI] Temporary Save clicked', { isGeoReady });
										await formik.setFieldValue("finalization", false, false);
										const errors = await formik.validateForm();
										console.log('[UI] Temporary Save validateForm errors', errors);
										if (Object.keys(errors || {}).length > 0) {
											touchAllFields(errors);
											return;
										}
										await formik.submitForm();
									}}
									disabled={!isGeoReady || isFinalized}
								>
									{t('Temporary Save')}
								</Button>
								
								<Button
									type="button"
									variant="contained"
									onClick={async () => {
										console.log('[UI] Finalization clicked', { isGeoReady });
										await formik.setFieldValue("finalization", true, false);
										const errors = await formik.validateForm();
										console.log('[UI] Finalization validateForm errors', errors);
										if (Object.keys(errors || {}).length > 0) {
											touchAllFields(errors);
											return;
										}
										await formik.submitForm();
									}}
									disabled={!isGeoReady || isFinalized}
								>
									{t('Finalization')}
								</Button>
							
							</Box>
						)}
					{/*{!onClose &&*/}
					{/*	<Button variant="contained"*/}
					{/*			onClick={() => console.log('todo: Maybe redirect in index page')}>*/}
					{/*		{t('Close')}*/}
					{/*	</Button>*/}
					{/*}*/}
				
				</Box>
				{onClose &&
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Button variant="contained" onClick={onClose}>
							{t('Close')}
						</Button>
					</Box>
				}
				{/*</Box>*/}
			</form>
		</FormikProvider>
	);
};

export default Preview;


